import { useMemo } from 'react';
import { formatNotification } from '../../features/chat/messageUtils';
import type { AppLanguage } from '../../i18n/locale';
import { relativeTime } from '../../screens/CustomerCabinet/customerCabinetShared';
import './notification-list.css';
import { CabinetEmptyState, CabinetLoadState } from '../CabinetEmptyState/CabinetEmptyState';

export interface UnifiedNotificationItemData {
  id: number;
  title: string;
  text: string;
  read_at?: string | null;
  created_at: string;
  isLink?: boolean;
  actionText?: string;
  notification_type?: string;
  target_id?: number | string | null;
  target_screen?: string;
  groupedIds?: number[];
}

export interface UnifiedNotificationListProps<T extends UnifiedNotificationItemData = UnifiedNotificationItemData> {
  items: T[];
  language?: AppLanguage;
  emptyText?: string;
  role?: 'carrier' | 'customer';
  className?: string;
  figmaNode?: string;
  state?: string;
  onItemClick: (item: T, groupedIds?: number[]) => void;
}

function extractSender(title: string): string | null {
  const match = title.match(/^(?:Новое сообщение от|Новые сообщения от|Сообщения от|Сообщение от|New message from|New messages from|Messages from|Message from)\s+(.+?)(?:\s*\(\d+\))?$/iu);
  return match ? match[1].trim() : null;
}

function groupNotifications<T extends UnifiedNotificationItemData>(items: T[], language: AppLanguage): Array<T & { groupedIds: number[] }> {
  const result: Array<T & { groupedIds: number[] }> = [];

  for (const item of items) {
    const sender = extractSender(item.title);
    const prev = result[result.length - 1];
    const prevSender = prev ? extractSender(prev.title) : null;

    const sameChat = Boolean(
      (sender && prevSender && sender.toLowerCase() === prevSender.toLowerCase()) ||
      (item.target_id && prev && prev.target_id && String(item.target_id) === String(prev.target_id) && item.notification_type === 'chat_message' && prev.notification_type === 'chat_message')
    );

    if (sameChat && prev) {
      prev.groupedIds.push(item.id);
      if (!item.read_at) {
        prev.read_at = null;
      }
    } else {
      result.push({
        ...item,
        groupedIds: [item.id],
      });
    }
  }

  for (const item of result) {
    const sender = extractSender(item.title);
    if (!sender) continue;
    const isUnread = !item.read_at;
    const count = item.groupedIds.length;

    if (isUnread) {
      if (count > 1) {
        item.title = language === 'en'
          ? `New messages from ${sender} (${count})`
          : `Новые сообщения от ${sender} (${count})`;
      } else {
        item.title = language === 'en'
          ? `New message from ${sender}`
          : `Новое сообщение от ${sender}`;
      }
    } else {
      if (count > 1) {
        item.title = language === 'en'
          ? `Messages from ${sender}`
          : `Сообщения от ${sender}`;
      } else {
        item.title = language === 'en'
          ? `Message from ${sender}`
          : `Сообщение от ${sender}`;
      }
    }
  }

  return result;
}

export function UnifiedNotificationList<T extends UnifiedNotificationItemData>({
  items,
  language = 'ru',
  emptyText,
  role,
  className = '',
  figmaNode,
  state = 'ready',
  onItemClick,
}: UnifiedNotificationListProps<T>) {
  const groupedItems = useMemo(() => groupNotifications(items, language), [items, language]);

  if (groupedItems.length === 0) {
    return state === 'loading' || state === 'error'
      ? <CabinetLoadState state={state} />
      : <CabinetEmptyState kind="notifications" title={emptyText} role={role} />;
  }

  return (
    <section className={`customer-notification-list ${className}`.trim()} data-figma-node={figmaNode} aria-label="Уведомления">
      {groupedItems.map((item) => {
        const loc = formatNotification(item.title, item.text, language);
        const isLink = Boolean(item.isLink);
        const actionText = item.actionText ?? (isLink
          ? (language === 'en' ? 'Open' : 'Открыть')
          : (language === 'en' ? 'Mark as read' : 'Пометить прочитанным'));

        return (
          <button
            className={[
              item.read_at ? 'is-read' : 'is-unread',
              isLink ? 'is-link' : '',
            ].filter(Boolean).join(' ')}
            key={item.id}
            type="button"
            onClick={() => onItemClick(item, item.groupedIds)}
          >
            <i />
            <span>
              <strong>{loc.title}</strong>
              <small>{loc.text}</small>
            </span>
            <span className="customer-notification-list__aside">
              <time dateTime={item.created_at}>{relativeTime(item.created_at)}</time>
              {!item.read_at || isLink ? (
                <span className="customer-notification-list__action">{actionText}</span>
              ) : null}
            </span>
          </button>
        );
      })}
    </section>
  );
}
